import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";
import { type PaymentTaxLine, splitFeeTotal } from "@/shared/utils/paymentTaxes";

export const PAYMENTS_QUOTES_ENDPOINTS = {
  QUOTES: "/internal/payments/quotes",
  /** Raw mid rate, no persisted quote — for rate-only surfaces like the quoter. */
  RATES: "/internal/payments/rates",
} as const;

/**
 * Request for the native FX quote engine (`POST /internal/payments/quotes`). Currencies are ISO codes
 * matching `wallets.currency` — the native engine prices per currency, not per country like the legacy PHP
 * proxy. Amounts are decimal STRINGS: they are money, and a JS `number` cannot carry them exactly.
 */
export interface NativeQuoteRequest {
  origin_currency: string;
  destination_currency: string;
  /** Gross debit, fee included. Send EITHER this or receive_amount, never both. */
  send_amount?: string;
  receive_amount?: string;
  service: "swap" | "payout";
  /**
   * Destination corridor, ISO3. Send together with `rail` and only with `service: "payout"`: it freezes the
   * dispersion commission next to the FX price, and a payout can only redeem a quote that carries it.
   */
  country?: string;
  /** Corridor rail (PIX/SPEI/CBU_CVU/BREB). Travels with `country`. */
  rail?: string;
  /** INTERNAL users only — members are pinned server-side from the JWT. */
  organization_id?: string;
}

/** The dispersion commission, in the DESTINATION currency. */
export interface NativeQuotePayoutFees {
  currency: string;
  total: string;
  coloca_total: string;
  provider_total: string;
  economic_model: string;
  /** Client-borne taxes, already inside `total`. Absent on a backend that predates taxes. */
  tax_total?: string;
  taxes?: PaymentTaxLine[];
}

export interface NativeQuote {
  id: string;
  direction: "FIXED_SEND" | "FIXED_RECEIVE";
  service: string;
  status: "ACTIVE" | "EXPIRED" | "CONSUMED";
  currency: { from: string; to: string };
  rate: {
    mid: string;
    effective: string;
    /** All-in rate to the beneficiary. Null when no corridor was quoted. */
    effective_payout?: string | null;
    provider_code: string;
  };
  amounts: {
    /** Gross debit in `currency.from`, conversion fee included. */
    send: string;
    /** `send − conversion fee`, in `currency.from`. */
    net: string;
    /**
     * Converted amount in `currency.to` — the GROSS the dispersion commission applies to, and what a payout
     * sends as `gross_amount`. NOT what the beneficiary receives; that is `payout_net`.
     */
    receive: string;
    /** What the beneficiary receives. Null when no corridor was quoted. */
    payout_net?: string | null;
  };
  /** The flat fields are the CONVERSION fee, always in `currency.from`. */
  fees: {
    currency: string;
    percentage_rate: string;
    percentage_amount: string;
    fixed_amount: string;
    total: string;
    /** Present when a corridor was quoted. Never summed with the fields above. */
    payout?: NativeQuotePayoutFees | null;
  };
  /** The corridor the dispersion commission was frozen for. */
  payout?: { country: string; rail: string } | null;
  wallets: { origin_id: string | null; destination_id: string | null };
  created_at: string;
  expires_at: string;
}

/**
 * One read model for the quote, so a contract change costs one file.
 *
 * The two fees live in different currencies and must never be summed: `conversion` is charged in the origin
 * currency, `payout` in the destination one. Degrades safely against a backend that has not shipped the
 * corridor leg yet.
 */
export interface QuoteFeeComponentView {
  currency: string;
  total: string;
}

export interface QuoteFeeView {
  /** Conversion (swap) fee in the origin currency. Null when nothing converts. */
  conversion: QuoteFeeComponentView | null;
  /** Dispersion commission in the destination currency, without the taxes below. */
  payout: QuoteFeeComponentView | null;
  /** Client-borne taxes on the dispersion, in the destination currency. */
  taxes: PaymentTaxLine[];
  /** Converted amount in the destination currency, before the commission. */
  converted: string | null;
  /** What the beneficiary actually receives. */
  beneficiaryReceives: string;
  isCrossCurrency: boolean;
}

export function quoteFeeView(quote: NativeQuote): QuoteFeeView {
  const isCrossCurrency = quote.currency.from !== quote.currency.to;
  const payoutFees = quote.fees.payout ?? null;

  // No corridor leg: the flat block is the only fee there is, and it is already
  // labelled with its own currency — which is what the UI must render.
  if (!payoutFees) {
    return {
      conversion: isCrossCurrency ? { currency: quote.fees.currency, total: quote.fees.total } : null,
      payout: isCrossCurrency ? null : { currency: quote.fees.currency, total: quote.fees.total },
      taxes: [],
      converted: isCrossCurrency ? quote.amounts.receive : null,
      beneficiaryReceives: quote.amounts.payout_net ?? quote.amounts.receive,
      isCrossCurrency,
    };
  }

  const { commission, taxes } = splitFeeTotal(payoutFees.total, payoutFees.taxes);
  return {
    conversion: isCrossCurrency ? { currency: quote.fees.currency, total: quote.fees.total } : null,
    payout: { currency: payoutFees.currency, total: commission },
    taxes,
    converted: isCrossCurrency ? quote.amounts.receive : null,
    beneficiaryReceives: quote.amounts.payout_net ?? quote.amounts.receive,
    isCrossCurrency,
  };
}

export interface NativeQuoteResponse {
  quote: NativeQuote;
}

export interface NativeRateResponse {
  base: string;
  quote: string;
  rate: string;
  provider_code: string;
  fetched_at: string;
}

/** HTTP client for the native FX quote engine. */
export class PaymentsQuotesService {
  static async createQuote(request: NativeQuoteRequest): Promise<AxiosResponse<NativeQuoteResponse>> {
    return HttpApiService.post<NativeQuoteResponse, NativeQuoteRequest>(
      PAYMENTS_QUOTES_ENDPOINTS.QUOTES,
      request,
    );
  }

  /**
   * Reads a quote back — the only authoritative way to tell whether it was consumed.
   *
   * `organization_id` is not optional in practice for INTERNAL staff: the endpoint resolves the scope of the
   * read from it and answers `400 organization_required` when it is missing. Members are pinned server-side
   * and must not send it. This is why the method existed unused and would have failed for half the operators
   * if anyone had called it.
   */
  static async getQuote(id: string, organizationId?: string): Promise<AxiosResponse<NativeQuoteResponse>> {
    return HttpApiService.get<NativeQuoteResponse>(`${PAYMENTS_QUOTES_ENDPOINTS.QUOTES}/${id}`, {
      params: organizationId ? { organization_id: organizationId } : undefined,
    });
  }

  /** Mid rate for a pair. Nothing is persisted, so it is safe to poll. */
  static async getRate(base: string, quote: string): Promise<AxiosResponse<NativeRateResponse>> {
    return HttpApiService.get<NativeRateResponse>(PAYMENTS_QUOTES_ENDPOINTS.RATES, {
      params: { base, quote },
    });
  }
}
